import {KTSVG} from '../../../../_metronic/helpers/components/KTSVG'
import Select, {MultiValue} from 'react-select'
import {useEffect, useState} from 'react'
import axios from 'axios'
import {DeleteConfirmationModal} from '../../../common/Models/confirmation-modal/DeleteConfirmationModal'
import toaster from '../../../../Utils/toaster'
import {CommonPaginationModel} from '../../../../Utils/commonPagination'
import {CommonLoading} from '../../../../Utils/commonLoading'

type RoutingListType = 'exclusion' | 'priority'

type RoutingListConfig = {
  listPath: string
  availablePath: string
  bulkSubmitPath: string
  deletePath: string
  title: string
  description: string
  modalTitle: string
  modalDescription: string
  addSuccessMessage: (count: number) => string
  addErrorMessage: string
  showPriorityOrder?: boolean
  showReason?: boolean
}

const REASON_MAX_LENGTH = 500

const ROUTING_LIST_CONFIG: Record<RoutingListType, RoutingListConfig> = {
  exclusion: {
    listPath: 'excludes',
    availablePath: 'excludes',
    bulkSubmitPath: 'excludes-list-bulk',
    deletePath: 'excludes',
    title: 'Exclusion List',
    description: "Interpreters from this list won't be available for calls",
    modalTitle: 'Add to Exclusion List',
    modalDescription: 'Select interpreters to exclude from calls for this customer',
    addSuccessMessage: (count) => `Successfully added ${count} interpreter(s) to exclusion list`,
    addErrorMessage: 'Failed to add interpreters!',
    showReason: true,
  },
  priority: {
    listPath: 'priority',
    availablePath: 'priority',
    bulkSubmitPath: 'priority-list-bulk',
    deletePath: 'priority',
    title: 'Priority List',
    description: 'Interpreters from this list are the priority for calls',
    modalTitle: 'Add to Priority List',
    modalDescription: 'Select interpreters to prioritize for calls for this customer',
    addSuccessMessage: (count) => `Successfully added ${count} interpreter(s) to priority list`,
    addErrorMessage: 'Failed to add interpreters to priority list!',
    showPriorityOrder: true,
  },
}

type Props = {
  customerId: string
  listType: RoutingListType
  modalId: string
}

export function CustomerRoutingListPanel({customerId, listType, modalId}: Props) {
  const API_URL = process.env.REACT_APP_API_URL
  const config = ROUTING_LIST_CONFIG[listType]

  const [isLoading, setIsLoading] = useState(false)
  const [currentItemCode, setCurrentItemCode] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [availableInterpreters, setAvailableInterpreters] = useState<{value: string; label: string}[]>([])
  const [interpreters, setInterpreters] = useState<any[]>([])
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedInterpreters, setSelectedInterpreters] =
    useState<MultiValue<{value: string; label: string}>>([])
  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const columnCount = 3 + (config.showPriorityOrder ? 1 : 0) + (config.showReason ? 1 : 0)

  const mapAvailableOptions = (rows: any[]) =>
    rows
      .map((d: any) => ({
        value: d.key ?? d.Key ?? '',
        label: d.value ?? d.Value ?? '',
      }))
      .filter((opt) => opt.value && opt.label)

  const fetchPriorityAvailableInterpreters = async () => {
    const [exclusionRes, priorityRes] = await Promise.all([
      axios.get(`${API_URL}/customer/excludes/${customerId}/available-interpreters`),
      axios
        .get(`${API_URL}/customer/priority/${customerId}`, {
          params: {page: 1, items_per_page: 5000},
        })
        .catch(() => ({data: {data: []}})),
    ])

    const priorityRows = priorityRes?.data?.data ?? priorityRes?.data?.Data ?? []
    const priorityIds = new Set(
      priorityRows
        .map((p: any) => p.fK_User ?? p.fk_User ?? p.FK_User ?? '')
        .filter(Boolean)
    )

    const exclusionRows = exclusionRes.data?.data ?? exclusionRes.data?.Data ?? []
    return mapAvailableOptions(exclusionRows).filter((opt) => !priorityIds.has(opt.value))
  }

  const fetchAvailableInterpreters = async () => {
    try {
      if (listType === 'priority') {
        const options = await fetchPriorityAvailableInterpreters()
        setAvailableInterpreters(options)
        return
      }

      const response = await axios.get(
        `${API_URL}/customer/excludes/${customerId}/available-interpreters`
      )
      const rows = response.data?.data ?? response.data?.Data ?? []
      setAvailableInterpreters(mapAvailableOptions(rows))
    } catch (error: any) {
      console.error('Error fetching available interpreters:', error)
      toaster('error', `Failed to load interpreters for ${config.title.toLowerCase()}`)
      setAvailableInterpreters([])
    }
  }

  const fetchInterpreterList = async (page: number, itemsPerPage: number) => {
    setIsLoading(true)
    try {
      const response = await axios.get(`${API_URL}/customer/${config.listPath}/${customerId}`, {
        params: {
          page,
          items_per_page: itemsPerPage,
        },
      })
      const {data, count} = response.data
      setInterpreters(data ?? [])
      setTotalItems(count || 0)
      setTotalPages(Math.ceil((count || 0) / itemsPerPage))
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!customerId) return
    fetchInterpreterList(currentPage, rowsPerPage)
    fetchAvailableInterpreters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, currentPage, rowsPerPage, listType])

  const resetFormFields = () => {
    setSelectedInterpreters([])
    setReason('')
  }

  const handleSubmit = async () => {
    if (!selectedInterpreters || selectedInterpreters.length === 0) {
      toaster('error', 'Please select at least one interpreter')
      return
    }

    setIsSubmitting(true)
    const data = {
      fK_Users: selectedInterpreters.map((item) => item.value),
      fK_Customer: Number(customerId),
      ...(config.showReason ? {reason: reason.trim() || null} : {}),
    }

    try {
      await axios.post(`${API_URL}/customer/${config.bulkSubmitPath}`, data)
      toaster('success', config.addSuccessMessage(selectedInterpreters.length))
      fetchInterpreterList(currentPage, rowsPerPage)
      fetchAvailableInterpreters()
      resetFormFields()
    } catch (error) {
      toaster('error', config.addErrorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const openDeleteModal = (code: string) => {
    setCurrentItemCode(code)
    setDeleteModalOpen(true)
  }

  const handleDeleteSubmit = async () => {
    if (!currentItemCode) return

    try {
      const response = await axios.put(`${API_URL}/customer/${config.deletePath}`, {
        code: currentItemCode,
        isDeleted: true,
      })
      const {status} = response.data
      if (status === 'S') {
        toaster('success', 'Successfully deleted')
        fetchInterpreterList(currentPage, rowsPerPage)
        fetchAvailableInterpreters()
      }
    } catch (error) {
      console.error(error)
    } finally {
      setDeleteModalOpen(false)
      setCurrentItemCode(null)
    }
  }

  return (
    <>
      <div className='modal fade' tabIndex={-1} id={modalId}>
        <div className='modal-dialog modal-md'>
          <div className='modal-content '>
            <div className='modal-header py-2'>
              <div className='d-flex flex-column'>
                <h4 className='modal-title'>{config.modalTitle}</h4>
                <span className='text-gray-500' style={{fontSize: '12px'}}>
                  {config.modalDescription}
                </span>
              </div>

              <div
                className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                data-bs-dismiss='modal'
                aria-label='Close'
                onClick={resetFormFields}
              >
                <KTSVG
                  path='/media/icons/duotune/arrows/arr061.svg'
                  className='svg-icon svg-icon-2x'
                />
              </div>
            </div>
            <div className='modal-body'>
              <div className='row g-4 '>
                <div className='col-sm-12 col-md-12 col-lg-12 '>
                  <div className=''>
                    <label className='form-label '>Interpreters</label>
                    <span className='text-gray-500 ms-2' style={{fontSize: '11px'}}>
                      (Select multiple interpreters)
                    </span>
                    <div>
                      <div className='w-100'>
                        <Select
                          className='react-select-styled react-select-solid react-select-sm'
                          classNamePrefix='react-select'
                          options={availableInterpreters}
                          placeholder='Select Interpreters'
                          styles={{
                            control: (provided: any) => ({
                              ...provided,
                              width: '100%',
                              border: '1px solid #e4e6ef',
                              minHeight: '38px',
                            }),
                            multiValue: (provided: any) => ({
                              ...provided,
                              backgroundColor: '#e1f0ff',
                            }),
                            multiValueLabel: (provided: any) => ({
                              ...provided,
                              color: '#3699ff',
                            }),
                            multiValueRemove: (provided: any) => ({
                              ...provided,
                              color: '#3699ff',
                              ':hover': {
                                backgroundColor: '#3699ff',
                                color: 'white',
                              },
                            }),
                          }}
                          onChange={(newValue: MultiValue<{value: string; label: string}>) =>
                            setSelectedInterpreters(newValue)
                          }
                          value={selectedInterpreters}
                          isMulti
                          closeMenuOnSelect={false}
                          isClearable
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {config.showReason && (
                  <div className='col-sm-12 col-md-12 col-lg-12'>
                    <label className='form-label'>Reason</label>
                    <span className='text-gray-500 ms-2' style={{fontSize: '11px'}}>
                      (Optional)
                    </span>
                    <textarea
                      className='form-control form-control-sm'
                      rows={3}
                      maxLength={REASON_MAX_LENGTH}
                      placeholder='Reason for excluding the selected interpreter(s)'
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                    <div className='text-gray-500 text-end' style={{fontSize: '11px'}}>
                      {reason.length}/{REASON_MAX_LENGTH}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='modal-footer py-3'>
              <button
                type='button'
                className='btn btn-light btn-sm'
                data-bs-dismiss='modal'
                onClick={resetFormFields}
              >
                Cancel
              </button>
              <button
                type='button'
                className='btn btn-primary btn-sm'
                data-bs-dismiss='modal'
                onClick={handleSubmit}
                disabled={!selectedInterpreters || selectedInterpreters.length === 0 || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm me-2'
                      role='status'
                      aria-hidden='true'
                    ></span>
                    Adding...
                  </>
                ) : (
                  <>
                    Add{' '}
                    {selectedInterpreters && selectedInterpreters.length > 0
                      ? `(${selectedInterpreters.length})`
                      : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className='border border-gray-500 border-dotted rounded p-5 mb-3'>
        <div className='d-flex justify-content-between align-items-center w-100'>
          <div className='d-flex flex-column'>
            <div className='d-flex fs-3 fw-semibold'>{config.title}</div>
            <span className='text-gray-500' style={{fontSize: '12px'}}>
              {config.description}
            </span>
          </div>

          <div className='d-flex align-items-center'>
            <button
              type='button'
              className='btn btn-sm btn-primary btn-icon'
              data-bs-toggle='modal'
              data-bs-target={`#${modalId}`}
              onClick={() => {
                resetFormFields()
                fetchAvailableInterpreters()
              }}
            >
              <i className='bi bi-plus fs-2'></i>
            </button>
          </div>
        </div>
        <div className='row g-4 '>
          <div className='py-0 pt-3'>
            <div className='table-responsive'>
              <table className='table table-striped table-rounded table-hover table-row-gray-300 align-middle gs-0 gy-2'>
                <thead>
                  <tr className='fw-semibold text-muted text-uppercase'>
                    {config.showPriorityOrder && <th className='min-w-50px'>Order</th>}
                    <th className='min-w-100px '>Full Name</th>
                    <th className='min-w-100px '>Email </th>
                    {config.showReason && <th className='min-w-150px'>Reason</th>}
                    <th className='min-w-100px text-end'>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {interpreters?.length > 0 ? (
                    interpreters.map((item: any, index: number) => (
                      <tr key={item.code} style={{cursor: 'pointer'}}>
                        {config.showPriorityOrder && (
                          <td>
                            <span className='text-gray-800 fs-6'>
                              {(currentPage - 1) * rowsPerPage + index + 1}
                            </span>
                          </td>
                        )}
                        <td>
                          <span className='text-gray-800 text-hover-primary fs-6'>{item?.userName}</span>
                        </td>
                        <td>
                          <span className='text-gray-800 text-hover-primary fs-6'>{item?.email}</span>
                        </td>
                        {config.showReason && (
                          <td>
                            {item?.reason ? (
                              <span className='text-gray-800 fs-6'>{item.reason}</span>
                            ) : (
                              <span className='text-gray-400 fs-6'>-</span>
                            )}
                          </td>
                        )}
                        <td className='text-end'>
                          <button
                            type='button'
                            className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm me-1 '
                            onClick={() => openDeleteModal(item?.code)}
                          >
                            <KTSVG
                              path='/media/icons/duotune/general/gen027.svg'
                              className='svg-icon-3'
                            />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={columnCount}>
                        <div className='py-5 d-flex flex-column align-content-center justify-content-center'>
                          <div className='text-center'>
                            <div className='symbol symbol-200px '>
                              <img src='/media/other/nodata.png' alt='' />
                            </div>
                          </div>
                          <div className='d-flex text-center w-100 align-content-center justify-content-center fw-semibold fs-3 text-gray-400'>
                            No matching records found
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {isLoading && <CommonLoading />}

          {totalItems > 0 && (
            <div className='pt-3'>
              <CommonPaginationModel
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                rowsPerPage={rowsPerPage}
                total={totalItems}
                onRowsPerPageChange={(newRowsPerPage) => {
                  setRowsPerPage(newRowsPerPage)
                  setCurrentPage(1)
                }}
              />
            </div>
          )}
        </div>
      </div>

      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          title='Confirmation'
          content={'Are you sure you would like to delete ?'}
          setOpen={setDeleteModalOpen}
          onSubmit={handleDeleteSubmit}
        />
      )}
    </>
  )
}
